import React, { ChangeEventHandler, useEffect, useState, useRef } from 'react';
import { Publish as PublishIcon } from '@mui/icons-material';
import { Box, CircularProgress, FormControl, FormControlProps } from '@mui/material';
import { captureException } from '@askporter/exception-logger';
import { UploadedFile } from '@askporter/utils';
import { Button, ButtonProps } from '../../';
import uploadFiles, { SimplifiedIAPIClient } from './uploadFiles';

interface SingleFileUploadButtonProps extends Omit<ButtonProps, 'onChange'> {
  onChange?: (uploadState: UploadedFile) => void;
  variant?: ButtonProps['variant'];
  label: string;
  t: (key: string, options?: Record<string, string | number>) => string;
  API: () => SimplifiedIAPIClient;
  formControlProps?: FormControlProps;
  disableToMargin?: boolean;
  disableBottomMargin?: boolean;
}

export const SingleFileUploadButton = ({
  onChange = () => ({}),
  variant = 'contained',
  label,
  sx = {},
  formControlProps = {},
  t,
  API,
  disableToMargin,
  disableBottomMargin,
  ...buttonProps
}: SingleFileUploadButtonProps): JSX.Element => {
  const ref = useRef<HTMLButtonElement>(null);
  const [startWidth, setStartWidth] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);
  const [fileUploadState, setFileUploadState] = useState<UploadedFile>();

  const handleFileAdd: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const files = e?.target?.files;
    if (!files || files.length === 0) return;

    setIsLoading(true);
    const uploadResult = await uploadFiles([files[0]], API, captureException);
    setFileUploadState(uploadResult[0]);
    setIsLoading(false);
  };

  useEffect(() => {
    if (fileUploadState) {
      onChange(fileUploadState);
      setFileUploadState(undefined);
    }
  }, [fileUploadState, onChange]);

  useEffect(() => {
    if (!isLoading) setStartWidth(ref?.current?.offsetWidth || 0);
  }, [isLoading, label]);

  return (
    <FormControl
      {...formControlProps}
      sx={{ ...(disableToMargin ? { mt: 0 } : {}), ...(disableBottomMargin ? { mb: 0 } : {}), ...sx }}
    >
      <Button
        component="label"
        disabled={isLoading}
        variant={variant}
        data-testid={`upload-button`}
        endIcon={<PublishIcon />}
        html-for="single-file-input"
        ref={ref}
        sx={{ ...(isLoading ? { width: startWidth ? `${startWidth}px` : undefined } : {}), ...sx }}
        {...buttonProps}
      >
        {isLoading && (
          <Box width="100%" display="flex" justifyContent="center">
            <CircularProgress data-testid="loading-indicator" />
          </Box>
        )}
        {!isLoading && (
          <>
            <input
              autoComplete="off"
              id="single-file-input"
              multiple={false}
              onChange={handleFileAdd}
              style={{ display: 'none' }}
              type="file"
            />
            {label || t('ns.common:file_upload:single:input_button')}
          </>
        )}
      </Button>
    </FormControl>
  );
};
